import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ScanSearch,
  Scale,
  FolderKanban,
  CreditCard,
  AlertTriangle,
  Globe,
  Settings,
  Shield,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth-context';
import { useTranslation } from '@/i18n/TranslationContext';

const baseNavKeys = [
  { key: 'nav_dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { key: 'nav_scans', icon: ScanSearch, path: '/scans' },
  { key: 'nav_subscriptions', icon: CreditCard, path: '/subscriptions' },
  { key: 'nav_fines', icon: AlertTriangle, path: '/fines' },
  { key: 'nav_public_scores', icon: Globe, path: '/public-scores' },
  { key: 'nav_settings', icon: Settings, path: '/settings' },
];

const legalNavKeys = [
  { key: 'nav_laws', icon: Scale, path: '/laws' },
  { key: 'nav_categories', icon: FolderKanban, path: '/categories' },
];

export function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isLegal = user?.role === 'legal_entry' || user?.role === 'super_admin';

  const toItem = (n) => ({ label: t(n.key), icon: n.icon, path: n.path });
  let allNavItems = baseNavKeys.map(toItem);
  if (isLegal) {
    allNavItems.splice(1, 0, ...legalNavKeys.map(toItem));
  }
  if (isAdmin) {
    allNavItems.push({ label: t('nav_admin'), icon: ShieldCheck, path: '/admin' });
  }

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="app-sidebar"
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-border bg-card transition-[width] duration-200',
          collapsed ? 'w-[68px]' : 'w-[240px]'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 px-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          {!collapsed && (
            <div className="flex flex-col animate-fade-in">
              <span className="text-sm font-semibold tracking-tight text-foreground">VRS</span>
              <span className="text-[10px] font-medium text-muted-foreground leading-none">Vendor Risk Scanner</span>
            </div>
          )}
        </div>

        <Separator className="opacity-50" />

        {/* Navigation */}
        <nav data-testid="sidebar-nav" className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {allNavItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            const Icon = item.icon;

            const button = (
              <button
                key={item.path}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-')}`}
                onClick={() => navigate(item.path)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-200',
                  isActive
                    ? 'bg-accent/60 text-foreground border border-border/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-transparent'
                )}
              >
                <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-primary' : '')} />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </button>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return button;
          })}
        </nav>

        {/* Collapse toggle */}
        <div className="border-t border-border p-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            data-testid="sidebar-collapse-toggle"
            className="w-full justify-center text-muted-foreground hover:text-foreground"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
